/**
 * FeedAvatar.js — EcoScienceInnova
 * Shared atom: avatar with initials and deterministic color hash.
 * Used by Feed.js (researchers) and CommunityFeed.js (patients).
 */
import React, { useState } from 'react';
import './feed.shared.css';

const AVATAR_COLORS = [
    '#FF6600', // eco-orange
    '#3b82f6', // blue
    '#22c55e', // green
    '#a855f7', // purple
    '#ec4899', // pink
    '#f59e0b', // amber
    '#0ea5e9', // sky
    '#14b8a6', // teal
];

/**
 * @param {string}  name      - Full name to derive initials and color
 * @param {number}  size      - Width/height in px (default: 42)
 * @param {string}  className - Additional CSS class
 * @param {string}  src       - Optional image URL — renders <img> instead
 * @param {function} onClick  - Optional click handler
 * @param {object}  style     - Additional styles
 */
const FeedAvatar = ({ name = '?', size = 42, className = '', src, onClick, style: additionalStyle }) => {
    const [imgError, setImgError] = useState(false);

    const commonStyle = {
        width: size,
        height: size,
        cursor: onClick ? 'pointer' : 'default',
        ...additionalStyle
    };

    if (src && !imgError) {
        let backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        // Remove /api if it's at the end
        if (backendUrl.endsWith('/api')) {
            backendUrl = backendUrl.slice(0, -4);
        }
        const fullSrc = src.startsWith('http') ? src : `${backendUrl}${src.startsWith('/') ? '' : '/'}${src}`;

        return (
            <img
                src={fullSrc}
                alt={name}
                className={`feed-avatar ${className}`}
                style={{ ...commonStyle, objectFit: 'cover' }}
                onClick={onClick}
                onError={() => setImgError(true)}
            />
        );
    }

    const initials = (name || '?')
        .split(' ')
        .map(w => w[0] || '')
        .slice(0, 2)
        .join('')
        .toUpperCase();

    const colorIndex = (name?.charCodeAt(0) || 0) % AVATAR_COLORS.length;
    const color = AVATAR_COLORS[colorIndex];

    return (
        <div
            className={`feed-avatar ${className}`}
            style={{
                ...commonStyle,
                background: color,
                fontSize: Math.round(size * 0.38),
            }}
            title={name}
            aria-label={name}
            onClick={onClick}
        >
            {initials}
        </div>
    );
};

export default FeedAvatar;
